import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import { dictionaryApi, disertationsApi } from '../../api/endpoints';
import {
    Field,
    FileField,
    FormActions,
    FormSection,
    MultiSelect,
    SingleSelect,
    TextInput,
} from '../../components/Form';
import { useUi } from '../../context/UiContext';
import styles from '../listPage.module.css';

const EMPTY = {
    name: '',
    description: '',
    endDescription: '',
    year: '',
    pages: '',
    napravlenie: '',
    spec: '',
    authors: [],
};

const DisertationForm = () => {
    const { id } = useParams();
    const history = useHistory();
    const { toast } = useUi();
    const isEdit = Boolean(id);

    const [values, setValues] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [currentFile, setCurrentFile] = useState(null);

    const [authorOptions, setAuthorOptions] = useState([]);
    const [napravlenieOptions, setNapravlenieOptions] = useState([]);
    const [specOptions, setSpecOptions] = useState([]);

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(0);
    const [errors, setErrors] = useState({});
    const [loadError, setLoadError] = useState(null);

    const set = useCallback((key, value) => {
        setValues((v) => ({ ...v, [key]: value }));
        setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
    }, []);

    useEffect(() => {
        let cancelled = false;

        Promise.all([dictionaryApi.options('dis-authors'), dictionaryApi.options('dis-napravlenie')])
            .then(([authors, napravlenie]) => {
                if (cancelled) return;
                setAuthorOptions(authors.map((a) => ({ value: a.id, label: a.name })));
                setNapravlenieOptions(napravlenie.map((n) => ({ value: n.id, label: n.name })));
            })
            .catch((e) => !cancelled && toast.error(`Не удалось загрузить справочники: ${e.message}`));

        return () => {
            cancelled = true;
        };
    }, [toast]);

    useEffect(() => {
        if (!isEdit) return undefined;

        let cancelled = false;
        setLoading(true);

        disertationsApi
            .get(id)
            .then((item) => {
                if (cancelled) return;
                setValues({
                    name: item.name || '',
                    description: item.description || '',
                    endDescription: item.endDescription || '',
                    year: item.year || '',
                    pages: item.pages || '',
                    napravlenie:
                        item.spec && item.spec.napravlenie ? item.spec.napravlenie.id : '',
                    spec: item.spec ? item.spec.id : '',
                    authors: item.authors.map((a) => a.id),
                });
                setCurrentFile(item.file_path || null);
            })
            .catch((e) => !cancelled && setLoadError(e))
            .finally(() => !cancelled && setLoading(false));

        return () => {
            cancelled = true;
        };
    }, [id, isEdit]);

    // Специальности зависят от направления.
    useEffect(() => {
        if (!values.napravlenie) {
            setSpecOptions([]);
            return undefined;
        }

        let cancelled = false;
        dictionaryApi
            .options('dis-spec', { parent: values.napravlenie })
            .then((items) => !cancelled && setSpecOptions(items.map((s) => ({ value: s.id, label: s.name }))))
            .catch((e) => !cancelled && toast.error(e.message));

        return () => {
            cancelled = true;
        };
    }, [values.napravlenie, toast]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setSaving(true);
        setProgress(0);

        const payload = {
            name: values.name,
            description: values.description,
            endDescription: values.endDescription,
            year: values.year,
            pages: values.pages,
            spec: values.spec || '',
            authors: values.authors,
        };
        if (file) payload.file = file;

        try {
            if (isEdit) {
                await disertationsApi.update(id, payload, setProgress);
                toast.success('Изменения сохранены');
                setFile(null);
                const fresh = await disertationsApi.get(id);
                setCurrentFile(fresh.file_path || null);
            } else {
                const created = await disertationsApi.create(payload, setProgress);
                toast.success('Диссертация добавлена');
                history.push(`/admin/disertations/${created.id}`);
            }
        } catch (err) {
            if (err.isValidation) {
                setErrors(err.errors || {});
                toast.error('Проверьте заполнение полей');
            } else {
                toast.error(err.message);
            }
        } finally {
            setSaving(false);
            setProgress(0);
        }
    };

    if (loadError) {
        return (
            <div className="adm-card adm-card-body">
                <p>Не удалось загрузить диссертацию: {loadError.message}</p>
                <NavLink to="/admin/disertations" className="adm-btn adm-btn-secondary">
                    К списку диссертаций
                </NavLink>
            </div>
        );
    }

    return (
        <div className={styles.formPage}>
            <NavLink to="/admin/disertations" className={styles.backLink}>
                ← К списку диссертаций
            </NavLink>

            <div className="adm-page-header">
                <div>
                    <h1 className="adm-page-title">
                        {isEdit ? 'Редактирование диссертации' : 'Новая диссертация'}
                    </h1>
                    {isEdit && <p className="adm-page-subtitle">{values.name}</p>}
                </div>
            </div>

            {loading ? (
                <div className="adm-card">
                    <div className={styles.formLoading}>Загрузка данных диссертации…</div>
                </div>
            ) : (
                <form className="adm-card" onSubmit={handleSubmit}>
                    <FormSection title="Основное">
                        <TextInput
                            id="dis-name"
                            label="Название"
                            required
                            value={values.name}
                            onChange={(e) => set('name', e.target.value)}
                            error={errors.name && errors.name[0]}
                        />

                        <div className="adm-grid-2">
                            <TextInput
                                id="dis-year"
                                label="Год"
                                value={values.year}
                                onChange={(e) => set('year', e.target.value)}
                                error={errors.year && errors.year[0]}
                                placeholder="например, 2023"
                            />
                            <TextInput
                                id="dis-pages"
                                label="Количество страниц"
                                value={values.pages}
                                onChange={(e) => set('pages', e.target.value)}
                                error={errors.pages && errors.pages[0]}
                            />
                        </div>
                    </FormSection>

                    <FormSection
                        title="Классификация"
                        description="Сначала выберите направление — список специальностей подставится под него."
                    >
                        <div className="adm-grid-2">
                            <SingleSelect
                                label="Направление"
                                options={napravlenieOptions}
                                value={values.napravlenie}
                                onChange={(v) => {
                                    set('napravlenie', v);
                                    set('spec', '');
                                }}
                            />
                            <SingleSelect
                                label="Специальность"
                                options={specOptions}
                                value={values.spec}
                                onChange={(v) => set('spec', v)}
                                placeholder={
                                    values.napravlenie ? '— выберите специальность —' : 'сначала выберите направление'
                                }
                                error={errors.spec && errors.spec[0]}
                            />
                        </div>

                        <MultiSelect
                            label="Авторы"
                            options={authorOptions}
                            value={values.authors}
                            onChange={(v) => set('authors', v)}
                        />
                    </FormSection>

                    <FormSection title="Тексты">
                        <Field label="Оглавление диссертации" error={errors.description && errors.description[0]}>
                            <ReactQuill
                                theme="snow"
                                value={values.description}
                                onChange={(v) => set('description', v)}
                            />
                        </Field>

                        <Field
                            label="Заключение диссертации"
                            error={errors.endDescription && errors.endDescription[0]}
                        >
                            <ReactQuill
                                theme="snow"
                                value={values.endDescription}
                                onChange={(v) => set('endDescription', v)}
                            />
                        </Field>
                    </FormSection>

                    <FormSection title="Файл">
                        <FileField
                            id="dis-file"
                            label="Документ (PDF)"
                            accept="application/pdf"
                            currentPath={currentFile}
                            file={file}
                            onChange={setFile}
                            progress={progress}
                            error={errors.file && errors.file[0]}
                            hint={isEdit ? 'Если файл не выбран, текущий останется без изменений.' : undefined}
                        />
                    </FormSection>

                    <FormActions>
                        <button type="submit" className="adm-btn adm-btn-primary" disabled={saving}>
                            {saving && <span className="adm-spinner" />}
                            {saving ? 'Сохранение…' : isEdit ? 'Сохранить изменения' : 'Добавить диссертацию'}
                        </button>
                        <NavLink to="/admin/disertations" className="adm-btn adm-btn-secondary">
                            Отмена
                        </NavLink>
                        {saving && progress > 0 && progress < 100 && (
                            <span className="adm-muted">Загрузка файла: {progress}%</span>
                        )}
                    </FormActions>
                </form>
            )}
        </div>
    );
};

export default DisertationForm;
